<i18n>
{
  "nl-BE": {
    "report": "Rapport",
    "low_level_warning_title": "Rapport is onbeschikbaar voor dit adres.",
    "low_level_warning_body": "Visuele controle nodig voor beoordeling overstromingsrisico.",
    "low_level_details_title": "Een visuele controle uitvoeren",
    "low_level_details_step1": "Stap 1: Navigeer op de kaart naar de locatie van het perceel. Zorg ervoor dat het perceel gecentreerd is en dat de straatnamen zichtbaar zijn.",
    "low_level_details_step2": "Stap 2: Maak een screenshot van het hele venster, inclusief adresbalk, perceel en straat naam.",
    "close": "Sluiten"
  },
  "en-BE": {
    "report": "Report",
    "low_level_warning_title": "Report unavailable for this address.",
    "low_level_warning_body": "Visual check needed for flood risk assessment.",
    "low_level_details_title": "How to provide a visual assessment",
    "low_level_details_step1": "Step 1:  Navigate on the map to the parcel location. Ensure the parcel is centered and street names are visible.",
    "low_level_details_step2": "Step 2: Take a screenshot of the entire window, including address bar, parcel, and street name.",
    "close": "Close"
  },
  "fr-BE": {
    "report": "Rapport",
    "low_level_warning_title": "Le rapport est indisponible pour cette adresse.",
    "low_level_warning_body": "Un contrôle visuel est nécessaire pour évaluer le risque d'inondation.",
    "low_level_details_title": "Comment fournir un contrôle visuel",
    "low_level_details_step1": "Etape 1: Naviguez sur la carte pour trouvez la parcelle d'intérêt. Assurez-vous que la parcelle soit bien centrée et les noms de rue soient visibles sur la carte.",
    "low_level_details_step2": "Etape 2: Réalisez une capture d'écran de la fenêtre complète en incluant la barre d'adresse, l'intégralité de la parcelle et les noms de rue.",
    "close": "Fermer"
  }
}
</i18n>

<template>
    <div id="sidebar">
        <div class="sidebar-back">
            <div class="container section p-3">
                <div class="columns is-multiline">
                    <div class="column is-12 pb-0">
                        <auto-complete
                            :placeholder="$store.state.address_text.full_address"
                            size="is-normal"
                            @selectedItem="selectAddress($event)"
                        />
                    </div>
                    <div v-if="getFeatures.level >= '1'" class="column is-12">
                        <b-button
                            tag="router-link"
                            class="is-primary is-outlined is-fullwidth"
                            :to="{
                                name: 'report',
                                params: { lang: $route.params.lang },
                                query: { ...$route.query, static: 'true' },
                            }"
                        >
                            {{ $t('report') }}
                        </b-button>
                    </div>
                    <div class="column is-12 pb-0 pt-5" v-else>
                        <b-notification
                            type="is-warning"
                            position="center"
                            :closable="false"
                        >
                            <p class="has-text-weight-bold">
                                {{ $t('low_level_warning_title') }}
                            </p>
                            <p>{{ $t('low_level_warning_body') }}</p>
                        </b-notification>
                        <b-notification
                            position="center"
                            class="content has-text-black"
                            :closable="false"
                        >
                            <p class="has-text-weight-bold">
                                {{ $t('low_level_details_title') }}
                            </p>

                            <ul class="ml-3">
                                <li>{{ $t('low_level_details_step1') }}</li>
                                <li>{{ $t('low_level_details_step2') }}</li>
                            </ul>
                        </b-notification>
                    </div>
                    <div
                        v-if="closable"
                        class="column is-12"
                        style="margin-top: -0.75rem"
                    >
                        <b-button class="is-primary is-fullwidth" @click="close">
                            {{ $t('close') }}
                        </b-button>
                    </div>
                </div>
            </div>
        </div>
        <div :key="'general'" class="category-info location mb-1">
            <div class="info-content">
                <div class="icon">
                    <img src="@/assets/icons/ic-location-blue.svg" alt="location icon" />
                </div>
                <div class="info-name">{{ getAddress.full_address }}</div>
            </div>
        </div>
        <b-collapse
            v-for="section of sections.filter(
                (section) => section.items.length || $route.query.showmissing
            )"
            :key="section.name"
            class="card"
            animation="slide"
            :open="openSection == section.name"
            @open="openSection = section.name"
        >
            <template #trigger="props">
                <div class="sidebar_item" role="button">
                    <span>
                        <img
                            v-if="section.name !== 'warnings' && section.icon"
                            class="icon is-medium"
                            :src="`${publicPath}/${section.icon}.svg`"
                        />
                        <span
                            v-if="section.name === 'warnings'"
                            class="icon is-medium my-2 mx-3 has-text-warning"
                        >
                            <i class="mdi mdi-alert mdi-24px"></i>
                        </span>

                        <span class="noselect has-text-weight-bold is-size-6">
                            {{ $t('section.' + section.name) }}
                        </span>
                    </span>
                    <span>
                        <span class="tag is-rounded">
                            {{ section.items.length }}
                        </span>
                        <i class="arrow" :class="props.open ? 'up' : 'down'"></i>
                    </span>
                </div>
            </template>
            <div class="card-content">
                <div class="content">
                    <section-item
                        v-for="item in section.items"
                        :key="item.name"
                        :item="item"
                        :orientation="'horizontal'"
                    ></section-item>
                </div>
            </div>
        </b-collapse>
    </div>
</template>

<script>
import AutoComplete from '@/shared/components/AutoComplete.vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import SectionItem from '@/shared/components/SectionItem.vue'
import { config } from '@/shared/assets/config/config'

export default {
    name: 'Sidebar',
    filters: {
        notnull: function(value) {
            if (value === null || value === undefined) return '-'
            else return value
        },
    },
    components: {
        AutoComplete,
        SectionItem,
    },
    props: {
        closable: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            openSection: null,
            publicPath: process.env.VUE_APP_FRONTEND_URL.replace(/\/$/, ''),
        }
    },
    computed: {
        ...mapGetters(['getFeatures', 'getValuation', 'getAddress']),
        sections() {
            if (this.getFeatures.level < '1') return []
            let x = 0
            let sectionNames = [
                'warnings',
                'address_location',
                'c_building',
                'energy',
                'transaction',
                'secondary_buildings',
                'c_parcels',
                'reconstruction_value',
                'economic_activity',
                'roof',
                'flood',
            ]
            let sections = []
            for (const sectionName of sectionNames) {
                let icon = config.filter(
                    (item) => (item.location === 'section') & (item.name === sectionName)
                )[0].icon
                icon = icon ? icon : null
                let sectionContent = {
                    name: sectionName,
                    items: config.filter(
                        (item) =>
                            item.sections.includes(sectionName) &&
                            !this.$config.HIDDEN_FEATURES.includes(item.name) &&
                            ((item.location === 'features'
                                ? this.getFeatures[item.name]
                                : this.getValuation[item.name]) ||
                                this.$route.query.showmissing)
                    ),
                    icon: icon,
                }
                sections.push(sectionContent)
            }
            return sections
        },
    },
    methods: {
        ...mapMutations(['UPDATE_ADDRESS']),
        ...mapActions(['updateBuildingId']),
        goBack() {
            this.$router.push({ path: '/' })
        },
        close() {
            this.$emit('close')
        },
        sleep(time) {
            return new Promise((res, rej) => {
                setTimeout(() => {
                    res()
                }, time)
            })
        },
        selectAddress(val) {
            this.UPDATE_ADDRESS({
                streetname: val.streetname,
                streetnumber: val.streetnumber,
                postalcode: val.postalcode,
                municipality: val.municipality,
            })
            this.updateBuildingId(val.building_id)
            let next_action = this.$route.params.action
            if (val.level !== '3' && this.$route.params.action === '3d') {
                next_action = '2d'
            }
            this.$router.push({
                name: 'action',
                params: {
                    lang: this.$route.params.lang,
                    action: next_action,
                },
                query: {
                    building_id: val.building_id,
                    streetname: val.streetname,
                    streetnumber: val.streetnumber,
                    postalcode: val.postalcode,
                    municipality: val.municipality,
                },
            })
        },
    },
}
</script>

<style lang="scss">
.surfaces {
    width: 22px;
}
.noselect {
    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-user-select: none; /* Safari */
    -khtml-user-select: none; /* Konqueror HTML */
    -moz-user-select: none; /* Old versions of Firefox */
    -ms-user-select: none; /* Internet Explorer/Edge */
    user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome, Edge, Opera and Firefox */
}
.sidebar_item {
    display: inline-flex;
    align-items: center;
    width: 100%;
    justify-content: space-between;
    padding: 0.75rem;

    span {
        display: inline-flex;
        align-items: center;
        gap: 0.75rem;
    }
}
</style>
